import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  FileTextOutlined,
  ProjectOutlined,
  TableOutlined,
} from '@ant-design/icons';
import AppBreadcrumb from '../../components/Breadcrumb';
import ParamListView from '../../components/ParamListView';
import { addCTFlow, getActionFieldRules, getCTFlow, getCTFlows, getCTModules, getCTNextStep, getCTSubmodules, updateCTFlow } from '../../services/testingApi';
import { getSelectedProject } from '../../services/authApi';
import { zinnextActionOptions } from '../../services/dropdownValues/testPageValues';


const { Text } = Typography;

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Test Cases', href: '/addTestCase' },
];

function normalizeText(value = '') {
  return String(value ?? '').trim();
}

function toOptions(items = [], key) {
  return items.map((item) => ({ value: item.id, label: item[key] }));
}

const flowFields = [
  { label: 'Step', value: 'step_order', width: 70 },
  { label: 'Action', value: 'actions', width: 140 },
  { label: 'Key', value: 'key', width: 160, ellipsis: true },
  { label: 'Value', value: 'value', ellipsis: true },
  { label: 'Description', value: 'description', ellipsis: true },
  {
    label: 'Scenario',
    value: 'scenario',
    width: 110,
    render: (v) =>
      v === 1 ? <Tag color="green">Positive</Tag> : <Tag color="red">Negative</Tag>,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// CHANGE 1 — Action-based field validation matrix
// true  = field is MANDATORY for this action
// false = field is NOT needed (will be disabled + placeholder updated)
// ─────────────────────────────────────────────────────────────────────────────
const EMPTY_FIELD_RULES = { key: false, value: false, expected: false };

// Returns the rules for the selected action.
// If no action is selected yet, nothing is mandatory and nothing is disabled.
function getFieldRules(actionFieldRules, action) {
  if (!action) return EMPTY_FIELD_RULES;
  return actionFieldRules[action] ?? EMPTY_FIELD_RULES;
}

function clearFieldErrors(form, fieldNames) {
  form.setFields(fieldNames.map((name) => ({ name, errors: [] })));
}
// ─────────────────────────────────────────────────────────────────────────────

export default function ZinnextTestForm() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEditing = Boolean(editId);

  const project = useMemo(() => getSelectedProject(), []);

  const [modules, setModules] = useState([]);
  const [submodules, setSubmodules] = useState([]);
  const [flowRows, setFlowRows] = useState([]);
  const [actionOptions, setActionOptions] = useState(zinnextActionOptions);
  const [actionFieldRules, setActionFieldRules] = useState({});

  const [modulesLoading, setModulesLoading] = useState(false);
  const [submodulesLoading, setSubmodulesLoading] = useState(false);
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const selectedModule = Form.useWatch('module', form);
  const selectedSubmodule = Form.useWatch('submodule', form);
  const selectedAction = Form.useWatch('actions', form);

  const hasModule = Boolean(selectedModule);
  const hasSubmodule = Boolean(selectedSubmodule);

  // ─────────────────────────────────────────────────────────────────────────
  // CHANGE 2 — Derive live field rules from the currently selected action.
  // This automatically updates whenever the user picks a different action.
  // ─────────────────────────────────────────────────────────────────────────
  const fieldRules = useMemo(() => getFieldRules(actionFieldRules, selectedAction), [actionFieldRules, selectedAction]);
  // ─────────────────────────────────────────────────────────────────────────

  const moduleOptions = useMemo(() => toOptions(modules, 'modulename'), [modules]);
  const submoduleOptions = useMemo(() => toOptions(submodules, 'submodulename'), [submodules]);
  const selectedModuleName = useMemo(
    () => modules.find((item) => item.id === selectedModule)?.modulename ?? '',
    [modules, selectedModule],
  );
  const selectedSubmoduleName = useMemo(
    () => submodules.find((item) => item.id === selectedSubmodule)?.submodulename ?? '',
    [selectedSubmodule, submodules],
  );

  // ---- loaders ----
  const loadActionFieldRules = useCallback(async () => {
    try {
      const res = await getActionFieldRules();
      if (Array.isArray(res?.options) && res.options.length > 0) {
        setActionOptions(res.options);
      }
      if (res?.rules && typeof res.rules === 'object') {
        setActionFieldRules(res.rules);
      }
    } catch (error) {
      console.error('Unable to load action field rules:', error);
      message.error('Unable to load action validation rules.');
    }
  }, []);

  const loadModules = useCallback(async (projectName) => {
    if (!projectName) {
      setModules([]);
      return;
    }
    setModulesLoading(true);
    try {
      const res = await getCTModules({ project: projectName, activeOnly: true });
      setModules(res?.items ?? []);
    } catch (error) {
      console.error('Unable to load modules:', error);
      message.error('Unable to load modules.');
    } finally {
      setModulesLoading(false);
    }
  }, []);

  const loadSubmodules = useCallback(async (projectName, moduleName) => {
    if (!projectName || !moduleName) {
      setSubmodules([]);
      return;
    }
    setSubmodulesLoading(true);
    try {
      const res = await getCTSubmodules({ project: projectName, module: moduleName, activeOnly: true });
      setSubmodules(res?.items ?? []);
    } catch (error) {
      console.error('Unable to load submodules:', error);
      message.error('Unable to load submodules.');
    } finally {
      setSubmodulesLoading(false);
    }
  }, []);

  const loadFlowsAndStep = useCallback(async (productId, moduleId, submoduleId, actions = '') => {
    if (!productId || !moduleId || !submoduleId) {
      setFlowRows([]);
      form.setFieldsValue({ step_order: undefined });
      return;
    }
    setFlowsLoading(true);
    try {
      const [flowsRes, stepRes] = await Promise.all([
        getCTFlows({
          product_parent_ID: productId,
          module_parent_ID: moduleId,
          submodule_parent_ID: submoduleId,
          actions,
          limit: 1000,
        }),
        getCTNextStep({ product_parent_ID: productId, module_parent_ID: moduleId, submodule_parent_ID: submoduleId }),
      ]);
      setFlowRows(flowsRes?.items ?? []);
      form.setFieldsValue({ step_order: stepRes?.step_order ?? 1 });
    } catch (error) {
      console.error('Unable to load flows:', error);
      message.error('Unable to load flows.');
    } finally {
      setFlowsLoading(false);
    }
  }, [form]);

  const loadActionFlows = useCallback(async (actions = '') => {
    if (!project?.id || !selectedModule || !selectedSubmodule) return;

    setFlowsLoading(true);
    try {
      const flowsRes = await getCTFlows({
        product_parent_ID: project.id,
        module_parent_ID: selectedModule,
        submodule_parent_ID: selectedSubmodule,
        actions,
        limit: 1000,
      });
      setFlowRows(flowsRes?.items ?? []);
    } catch (error) {
      console.error('Unable to load action flows:', error);
      message.error('Unable to load action steps.');
    } finally {
      setFlowsLoading(false);
    }
  }, [project?.id, selectedModule, selectedSubmodule]);

  const loadEditFlow = useCallback(async () => {
    if (!editId || !project?.name) return;

    setEditLoading(true);
    try {
      const flow = await getCTFlow(editId);
      await loadSubmodules(project.name, flow.module);
      const flowsRes = await getCTFlows({
        product_parent_ID: flow.product_parent_ID,
        module_parent_ID: flow.module_parent_ID,
        submodule_parent_ID: flow.submodule_parent_ID,
        limit: 1000,
      });
      setFlowRows(flowsRes?.items ?? []);
      form.setFieldsValue({
        module: flow.module_parent_ID,
        submodule: flow.submodule_parent_ID,
        step_order: flow.step_order,
        actions: flow.actions,
        key: flow.key,
        value: flow.value,
        expected: flow.expected,
        description: flow.description,
        scenario: flow.scenario === 1,
        active: Number(flow.active) !== 0,
      });
    } catch (error) {
      console.error('Unable to load test flow:', error);
      message.error('Unable to load test flow for edit.');
      navigate('/viewTestCase');
    } finally {
      setEditLoading(false);
    }
  }, [editId, form, loadSubmodules, navigate, project?.name]);

  useEffect(() => {
    if (!project) {
      navigate('/select-project', { replace: true });
      return;
    }
    Promise.resolve().then(async () => {
      await loadActionFieldRules();
      await loadModules(project.name);
      if (editId) await loadEditFlow();
    });
  }, [project, navigate, loadActionFieldRules, loadModules, editId, loadEditFlow]);

  // ---- change handlers ----
  const handleModuleChange = (value) => {
    const moduleName = modules.find((item) => item.id === value)?.modulename ?? '';
    form.setFieldsValue({
      module: value,
      submodule: undefined,
      step_order: undefined,
      actions: undefined,
      key: undefined,
      value: undefined,
      expected: undefined,
    });
    clearFieldErrors(form, ['actions', 'key', 'value', 'expected']);
    setSubmodules([]);
    setFlowRows([]);
    loadSubmodules(project?.name, moduleName);
  };

  const handleSubmoduleChange = (value) => {
    form.setFieldsValue({ submodule: value, actions: undefined, key: undefined, value: undefined, expected: undefined });
    clearFieldErrors(form, ['actions', 'key', 'value', 'expected']);
    loadFlowsAndStep(project?.id, selectedModule, value);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // CHANGE 3 — When action changes, clear fields that are NOT needed for the
  // newly selected action so stale data is never saved to the DB.
  // ─────────────────────────────────────────────────────────────────────────
  const handleActionChange = (value) => {
    const rules = getFieldRules(actionFieldRules, value);
    form.setFieldsValue({
      actions: value,
      key:      rules.key      ? form.getFieldValue('key')      : undefined,
      value:    rules.value    ? form.getFieldValue('value')    : undefined,
      expected: rules.expected ? form.getFieldValue('expected') : undefined,
    });
    clearFieldErrors(form, ['key', 'value', 'expected']);
    loadActionFlows(value || '');
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ---- save & add ----
  const onSaveAndAdd = async (values) => {
    const payload = {
      product_parent_ID: Number(project?.id),
      module_parent_ID: Number(values.module),
      submodule_parent_ID: Number(values.submodule),
      description: normalizeText(values.description),
      actions: normalizeText(values.actions),
      key: normalizeText(values.key),
      value: normalizeText(values.value),
      expected: normalizeText(values.expected),
      scenario: values.scenario ? 1 : 0,
      active: values.active ? 1 : 0,
    };

    setSubmitting(true);
    try {
      if (isEditing) {
        await updateCTFlow(editId, {
          ...payload,
          step_order: Number(values.step_order),
        });
        message.success('Test case updated successfully.');
        navigate('/viewTestCase');
        return;
      }

      await addCTFlow(payload);
      message.success(`Step saved (#${values.step_order}).`);
      form.setFieldsValue({
        actions: undefined,
        key: undefined,
        value: undefined,
        expected: undefined,
        description: undefined,
      });
      await loadFlowsAndStep(payload.product_parent_ID, payload.module_parent_ID, payload.submodule_parent_ID);
    } catch (error) {
      console.error('Unable to save test case:', error);
      message.error(isEditing ? 'Unable to update test case.' : 'Unable to save step.');
    } finally {
      setSubmitting(false);
    }
  };

  const clearStepFields = () => {
    if (isEditing) {
      navigate('/viewTestCase');
      return;
    }
    form.resetFields();
    setSubmodules([]);
    setFlowRows([]);
    setSubmodulesLoading(false);
    setFlowsLoading(false);
  };

  if (!project) return null;

  return (
    <main className="jobs-list-page">
      <Row align="middle" justify="space-between" className="breadcrumb-action-wrapper">
        <Col>
          <AppBreadcrumb items={breadcrumbItems} />
        </Col>
        <Col>
          <Space size={6} className="zt-section-title">
            <ProjectOutlined />
            <Text strong>{project.name}</Text>
          </Space>
        </Col>
      </Row>

      <div className="zt-page">
        <Form
          form={form}
          layout="vertical"
          colon={false}
          className="v1-form"
          initialValues={{ scenario: true, active: true }}
          onFinish={onSaveAndAdd}
          autoComplete="off"
          disabled={editLoading}
        >
          {/* ---------- CARD 1: Module / Submodule / Scenario ---------- */}
          <Card
            className="v1-card zt-fall"
            title={(
              <Space size={8} className="zt-section-title">
                <span className="zt-step-tag">1</span>
                <span>Module &amp; Submodule</span>
              </Space>
            )}
          >
            <Row gutter={[24, 0]} align="bottom">
              <Col xs={24} md={6}>
                <Form.Item label="Module" name="module">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={modulesLoading}
                    placeholder="Select module"
                    options={moduleOptions}
                    onChange={handleModuleChange}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Submodule" name="submodule">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    disabled={!hasModule}
                    loading={submodulesLoading}
                    placeholder={hasModule ? 'Select submodule' : 'Select module first'}
                    options={submoduleOptions}
                    onChange={handleSubmoduleChange}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Scenario" name="scenario" valuePropName="checked">
                  <Switch
                    disabled={!hasSubmodule}
                    checkedChildren="Positive"
                    unCheckedChildren="Negative"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                {/* Active toggle — no disabled, always editable, defaults ON */}
                <Form.Item label="Active" name="active" valuePropName="checked">
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* ---------- CARD 2: Step fields ---------- */}
          {hasSubmodule && (
            <Card
              key={`${selectedModule}-${selectedSubmodule}`}
              className="v1-card zt-fall"
              title={(
                <Space size={8} className="zt-section-title">
                  <span className="zt-step-tag">2</span>
                  <FileTextOutlined />
                  <span>Step Details</span>
                </Space>
              )}
              extra={<Text type="secondary">{selectedModuleName} / {selectedSubmoduleName}</Text>}
            >
              <Row gutter={[24, 0]}>
                <Col xs={24} md={6}>
                  <Form.Item label="Step Order" name="step_order">
                    <Input disabled placeholder={flowsLoading ? 'Loading…' : 'Auto'} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={18}>
                  <Form.Item
                    label="Action"
                    name="actions"
                    rules={[{ required: true, message: 'Mandatory Field' }]}
                  >
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder="Select action"
                      options={actionOptions}
                      onChange={handleActionChange}
                    />
                  </Form.Item>
                </Col>

                {/* ───────────────────────────────────────────────────────────
                    CHANGE 4 — Key, Value, Expected now have dynamic rules:
                      • required   → red * label + blocks save if empty
                      • !required  → disabled input + "Not needed" placeholder
                    ─────────────────────────────────────────────────────────── */}
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Key"
                    name="key"
                    rules={[
                      {
                        required: fieldRules.key,
                        message: 'Key is required for this action',
                      },
                    ]}
                  >
                    <Input
                      maxLength={255}
                      disabled={selectedAction && !fieldRules.key}
                      placeholder={
                        !selectedAction
                          ? 'Select an action first'
                          : fieldRules.key
                          ? 'Required — Enter selector / locator'
                          : 'Not needed for this action'
                      }
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Value"
                    name="value"
                    rules={[
                      {
                        required: fieldRules.value,
                        message: 'Value is required for this action',
                      },
                    ]}
                  >
                    <Input.TextArea
                      rows={1}
                      autoSize={{ minRows: 1, maxRows: 4 }}
                      disabled={selectedAction && !fieldRules.value}
                      placeholder={
                        !selectedAction
                          ? 'Select an action first'
                          : fieldRules.value
                          ? 'Required — Enter value'
                          : 'Not needed for this action'
                      }
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Expected"
                    name="expected"
                    rules={[
                      {
                        required: fieldRules.expected,
                        message: 'Expected result is required for this action',
                      },
                    ]}
                  >
                    <Input.TextArea
                      rows={2}
                      disabled={selectedAction && !fieldRules.expected}
                      placeholder={
                        !selectedAction
                          ? 'Select an action first'
                          : fieldRules.expected
                          ? 'Required — Enter expected result'
                          : 'Not needed for this action'
                      }
                    />
                  </Form.Item>
                </Col>
                {/* ─────────────────────────────────────────────────────────── */}

                <Col xs={24} md={12}>
                  <Form.Item label="Description" name="description">
                    <Input.TextArea rows={2} maxLength={255} showCount placeholder="Enter description" />
                  </Form.Item>
                </Col>
              </Row>

              <Space className="zt-actions">
                <Button onClick={clearStepFields}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={submitting || editLoading}>
                  {isEditing ? 'Update Test Case' : 'Save & Continue'}
                </Button>
              </Space>
            </Card>
          )}

          {/* ---------- Added steps table ---------- */}
          {hasSubmodule && (
            <Card
              className="v1-card zt-fall"
              title={(
                <Space size={6} className="zt-section-title">
                  <TableOutlined />
                  <span>Added Steps</span>
                </Space>
              )}
              extra={<Tag>{selectedAction ? `${flowRows.length} ${selectedAction} step(s)` : `${flowRows.length} step(s)`}</Tag>}
            >
              <ParamListView
                listName="Added Steps"
                loading={flowsLoading}
                fields={flowFields}
                dataSource={flowRows}
                current={1}
                pageSize={Math.max(flowRows.length, 1)}
                total={flowRows.length}
                rowSelection={false}
                className="zt-list-plain zt-list-static"
              />
            </Card>
          )}
        </Form>
      </div>
    </main>
  );
}
